import { StarFilled, StarOutlined } from '@ant-design/icons';
import { useCallback, useMemo } from 'react';

import type { SelectOptionT } from '@/components/Select/types';
import Tooltip from '@/components/Tooltip/TooltipV2';
import { MainText } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import { NOT_ASSIGNED_LABEL } from '@/configs/reserved-word';
import useCompanyStore from '@/features/Account/store';
import {
  type COMPANY_ACTIVE_STATUS,
  COMPANY_STATUS_MAP,
} from '@/features/Account/types/constants';
import AgentDropdown from '@/features/Agent/components/AgentDropdown';
import AttrSelectDropdown from '@/features/Commons/Dropdown/AttrSelectDropdown';
import SentimentBadge from '@/features/Feedback/field/Sentiment/SentimentBadge';
import useColor from '@/hooks/domain/useColor';
import useCompanyAttributes from '@/hooks/domain/useCompanyAttributes';
import useUserRole from '@/hooks/domain/useUserRole';
import useColumnConfigStore from '@/stores/DataTable/ColumnConfigStore';
import usePopupStore from '@/stores/pure/PopupStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { EllipsisDiv } from '@/styles/style';
import { ACCOUNT_COLUMNS } from '@/types/filterKeyConst';
import { readableFormat } from '@/utils/time';
import { DEFAULT_HIGHLIGHT_TARGET, formatNumber } from '@/utils/util';

const useCompanyHeader = (): DataTableColumnT[] => {
  const {
    industryOptions,
    sizeOptions,
    planOptions,
    ratingOptions,
    accountTypeOptions,
    countryOptions,
    statusOptions,
    agentOptions,
    accountStageOptions,
    accountPrimaryUsecseOptions,
  } = useCompanyAttributes();

  const { account_view } = useColumnConfigStore((state) => state.columnConfigs);

  const defaultColumns: DataTableColumnT[] = useMemo(() => {
    return [
      {
        columnKey: ACCOUNT_COLUMNS.ACCOUNT_STARRED,
        columnName: 'Key account',
        isDefaultColumn: true,
        hiddenColumnName: true,
        visible: true,
        width: 24,
        filterable: true,
        filterList: [
          {
            columnKey: ACCOUNT_COLUMNS.ACCOUNT_STARRED,
            filterKey: 'true',
            filterName: 'Starred',
            filterOptionNode: 'Starred',
          },
          {
            columnKey: ACCOUNT_COLUMNS.ACCOUNT_STARRED,
            filterKey: 'false',
            filterName: 'Unstarred',
            filterOptionNode: 'Unstarred',
          },
        ],
      },
      {
        columnKey: ACCOUNT_COLUMNS.NAME,
        columnName: 'Name',
        isDefaultColumn: true,
        width: 300,
        visible: true,
        sortKey: ACCOUNT_COLUMNS.NAME,
        orderable: true,
        orderType: 'string',
      },
      {
        columnKey: ACCOUNT_COLUMNS.SENTIMENT,
        columnName: 'Recent sentiment',
        getHeaderNode() {
          return (
            <Tooltip tooltipNode="The key sentiment from the most recent customer interaction.">
              <span>Recent sentiment</span>
            </Tooltip>
          );
        },
        width: 200,
        visible: true,
      },
      {
        columnKey: ACCOUNT_COLUMNS.INDUSTRY,
        columnName: 'Industry',
        width: 150,
        visible: false,
        searchable: true,
        filterable: true,
        filterList: industryOptions.map((option) => ({
          columnKey: ACCOUNT_COLUMNS.INDUSTRY,
          filterKey: option.value,
          filterName: option.searchName,
          filterOptionNode: option.searchName,
        })),
      },
      {
        columnKey: ACCOUNT_COLUMNS.EMPLOYEE_COUNT,
        columnName: 'Employees',
        width: 150,
        visible: false,
        sortKey: ACCOUNT_COLUMNS.EMPLOYEE_COUNT,
        orderable: true,
        orderType: 'number',
      },
      {
        columnKey: ACCOUNT_COLUMNS.SIZE,
        columnName: 'Size',
        width: 200,
        visible: false,
        searchable: true,
        filterable: true,
        filterList: sizeOptions.map((option) => {
          return {
            columnKey: ACCOUNT_COLUMNS.SIZE,
            filterKey: option.value,
            filterName: option.searchName,
            filterOptionNode: option.searchName,
          };
        }),
      },
      {
        columnKey: ACCOUNT_COLUMNS.MONTHLY_SPEND,
        columnName: 'Monthly spend',
        width: 150,
        visible: false,
        sortKey: ACCOUNT_COLUMNS.MONTHLY_SPEND,
        orderable: true,
        orderType: 'number',
      },
      {
        columnKey: ACCOUNT_COLUMNS.ANNUAL_REVENUE,
        columnName: 'Annual revenue',
        width: 150,
        visible: false,
        sortKey: ACCOUNT_COLUMNS.ANNUAL_REVENUE,
        orderable: true,
        orderType: 'number',
      },
      {
        columnKey: ACCOUNT_COLUMNS.PLAN,
        columnName: 'Plan',
        width: 150,
        visible: false,
        searchable: true,
        filterable: true,
        filterList: planOptions.map((option) => {
          return {
            columnKey: ACCOUNT_COLUMNS.PLAN,
            filterKey: option.value,
            filterName: option.searchName,
            filterOptionNode: option.searchName,
          };
        }),
      },
      {
        columnKey: ACCOUNT_COLUMNS.RATING,
        columnName: 'Rating',
        width: 150,
        visible: false,
        searchable: true,
        filterable: true,
        filterList: ratingOptions.map((option) => {
          return {
            columnKey: ACCOUNT_COLUMNS.RATING,
            filterKey: option.value,
            filterName: option.searchName,
            filterOptionNode: option.searchName,
          };
        }),
      },
      {
        columnKey: ACCOUNT_COLUMNS.TYPE,
        columnName: 'Type',
        width: 150,
        visible: false,
        searchable: true,
        filterable: true,
        filterList: accountTypeOptions.map((option) => {
          return {
            columnKey: ACCOUNT_COLUMNS.TYPE,
            filterKey: option.value,
            filterName: option.searchName,
            filterOptionNode: option.searchName,
          };
        }),
      },
      {
        columnKey: ACCOUNT_COLUMNS.STAGE,
        columnName: 'Stage',
        width: 150,
        visible: false,
        searchable: true,
        filterable: true,
        filterList: accountStageOptions.map((option) => {
          return {
            columnKey: ACCOUNT_COLUMNS.STAGE,
            filterKey: option.value,
            filterName: option.searchName,
            filterOptionNode: option.searchName,
          };
        }),
      },
      {
        columnKey: ACCOUNT_COLUMNS.PRIMARY_USECASE,
        columnName: 'Primary Usecase',
        width: 150,
        visible: false,
        searchable: true,
        filterable: true,
        filterList: accountPrimaryUsecseOptions.map((option) => {
          return {
            columnKey: ACCOUNT_COLUMNS.PRIMARY_USECASE,
            filterKey: option.value,
            filterName: option.searchName,
            filterOptionNode: option.searchName,
          };
        }),
      },
      {
        columnKey: ACCOUNT_COLUMNS.COUNTRY,
        columnName: 'Country',
        width: 160,
        visible: false,
        searchable: true,
        filterable: true,
        filterList: countryOptions.map((option) => {
          return {
            columnKey: ACCOUNT_COLUMNS.COUNTRY,
            filterKey: option.value,
            filterName: option.searchName,
            filterOptionNode: option.searchName,
          };
        }),
      },
      {
        columnKey: ACCOUNT_COLUMNS.FEEDBACK_COUNT,
        columnName: 'Feedback',
        width: 150,
        visible: false,
      },
      {
        columnKey: ACCOUNT_COLUMNS.NPS,
        columnName: 'NPS Score',
        width: 150,
        visible: false,
      },
      {
        columnKey: ACCOUNT_COLUMNS.CES,
        columnName: 'CES Score',
        width: 150,
        visible: false,
      },
      {
        columnKey: ACCOUNT_COLUMNS.CSAT,
        columnName: 'CSAT Score',
        width: 150,
        visible: false,
      },
      {
        columnKey: ACCOUNT_COLUMNS.ACTIVE_STATUS,
        columnName: 'Active status',
        width: 150,
        visible: false,
        sortKey: ACCOUNT_COLUMNS.ACTIVE_STATUS,
        orderable: true,
        orderType: 'string',
        filterable: true,
        filterList: statusOptions.map((option) => ({
          columnKey: ACCOUNT_COLUMNS.ACTIVE_STATUS,
          filterKey: option.value,
          filterName: option.searchName,
          filterOptionNode: option.searchName,
        })),
      },
      {
        columnKey: ACCOUNT_COLUMNS.CREATED_AT,
        columnName: 'Created date',
        width: 150,
        visible: false,
        sortKey: ACCOUNT_COLUMNS.CREATED_AT,
        orderable: true,
        orderType: 'date',
      },
      {
        columnKey: ACCOUNT_COLUMNS.LAST_ACTIVITY,
        columnName: 'Last activity date',
        width: 150,
        visible: false,
      },
      {
        columnKey: ACCOUNT_COLUMNS.MANAGER,
        columnName: 'Agent',
        width: 150,
        visible: false,
        searchable: true,
        filterable: true,
        filterList: agentOptions.map((option) => {
          return {
            columnKey: ACCOUNT_COLUMNS.MANAGER,
            filterKey: option.value,
            filterName: option.searchName,
            filterOptionNode: option.searchName,
          };
        }),
      },
      {
        columnKey: ACCOUNT_COLUMNS.KEY,
        columnName: 'Key',
        width: 150,
        visible: false,
      },
    ];
  }, [
    industryOptions,
    sizeOptions,
    planOptions,
    ratingOptions,
    accountTypeOptions,
    accountStageOptions,
    accountPrimaryUsecseOptions,
    countryOptions,
    statusOptions,
    agentOptions,
  ]);

  const columns = useMemo(() => {
    const fullColumns = [...defaultColumns];
    account_view.forEach((config) => {
      const targetIndex = fullColumns.findIndex(
        (col) => col.columnKey === config.name,
      );
      if (targetIndex === -1) return;
      fullColumns[targetIndex] = {
        ...fullColumns[targetIndex],
        width: config.width,
        visible: config.visible,
        columnOrderIndex: config.order,
      };
    });
    return fullColumns;
  }, [account_view, defaultColumns]);

  return columns;
};

const useCompanyTableData = (
  data: PaginatedData<CompanyT>[],
): DataTableRowT[] => {
  const { companyMap, setSelectedCompany, changeCompanyAttr } = useCompanyStore(
    ({ companyMap, setSelectedCompany, changeCompanyAttr }) => ({
      companyMap,
      setSelectedCompany,
      changeCompanyAttr,
    }),
  );
  const updateWorkspaceFieldOptions = useWorkspaceStore(
    (state) => state.updateWorkspaceFieldOptions,
  );
  const setPopup = usePopupStore((state) => state.setPopup);

  const color = useColor();
  const { isEditor } = useUserRole();
  const {
    industryOptions,
    sizeOptions,
    planOptions,
    ratingOptions,
    accountTypeOptions,
    countryOptions,
    statusOptions,
    accountStageOptions,
    accountPrimaryUsecseOptions,
  } = useCompanyAttributes();

  const handleOptionDelete = useCallback(
    (
      option: SelectOptionT,
      target: WorkspaceConfigsFields,
      itemName: string,
    ) => {
      setPopup({
        popupTheme: 'negative',
        type: 'confirm',
        title: `Delete the ${itemName}`,
        message: `Are you sure you want to delete this ${itemName}?`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        onConfirm: () => {
          updateWorkspaceFieldOptions(4, target, option.searchName);
        },
      });
    },
    [setPopup, updateWorkspaceFieldOptions],
  );

  const result: DataTableRowT[] = useMemo(() => {
    return data.reduce((acc, value) => {
      const company = companyMap[value.alias];
      if (!company) return acc;
      acc.push({
        onRowClick: (key) => setSelectedCompany(key),
        rowKey: company.alias,
        rowData: [
          {
            columnKey: ACCOUNT_COLUMNS.ACCOUNT_STARRED,
            children: (
              <>
                {company.favorite ? (
                  <StarFilled
                    style={{
                      color: color.yellow500,
                      cursor: isEditor ? 'pointer' : 'unset',
                    }}
                    onClick={() =>
                      isEditor &&
                      changeCompanyAttr(company.alias, 'favorite', false)
                    }
                  />
                ) : (
                  <StarOutlined
                    style={{
                      color: color.yellow500,
                      cursor: isEditor ? 'pointer' : 'unset',
                    }}
                    onClick={() =>
                      isEditor &&
                      changeCompanyAttr(company.alias, 'favorite', true)
                    }
                  />
                )}
              </>
            ),
            rawData: String(company.favorite),
          },
          {
            columnKey: ACCOUNT_COLUMNS.NAME,
            children: (
              <EllipsisDiv>
                <Typo
                  type="label2"
                  weight="m"
                  color={color.gray700}
                  className={DEFAULT_HIGHLIGHT_TARGET}
                >
                  {company.name}
                </Typo>
              </EllipsisDiv>
            ),
            onCellClick: () => setSelectedCompany(company.alias),
            rawData: company.name,
          },
          {
            columnKey: ACCOUNT_COLUMNS.SENTIMENT,
            children: (
              <EllipsisDiv>
                {company.latest_sentiment && (
                  <Tooltip tooltipNode="The key sentiment from the most recent customer interaction.">
                    <SentimentBadge type={company.latest_sentiment} />
                  </Tooltip>
                )}
              </EllipsisDiv>
            ),
            rawData: company.latest_sentiment || '',
          },
          {
            columnKey: ACCOUNT_COLUMNS.INDUSTRY,
            children: (
              <AttrSelectDropdown
                name={`company-list-${company.alias}-industry-dropdown`}
                view="list"
                selected={company.industry || null}
                options={industryOptions}
                onSelect={(industry) =>
                  changeCompanyAttr(
                    company.alias,
                    'industry',
                    industry,
                  )
                }
                onCreate={async (industry) => {
                  await updateWorkspaceFieldOptions(1, 'industry', industry);
                  changeCompanyAttr(
                    company.alias,
                    'industry',
                    industry,
                  );
                }}
                onDelete={(option) =>
                  handleOptionDelete(option, 'industry', 'Industry item')
                }
                onClear={() =>
                  changeCompanyAttr(company.alias, 'industry', null)
                }
              />
            ),
            rawData: company.industry,
          },
          {
            columnKey: ACCOUNT_COLUMNS.EMPLOYEE_COUNT,
            children: (
              <MainText>
                {company.employee_count
                  ? formatNumber(company.employee_count)
                  : company.employee_count}
              </MainText>
            ),
            rawData: company.employee_count,
          },
          {
            columnKey: ACCOUNT_COLUMNS.SIZE,
            children: (
              <AttrSelectDropdown
                name={`company-list-${company.alias}-size-dropdown`}
                view="list"
                selected={company.size || null}
                options={sizeOptions}
                onSelect={(size) =>
                  changeCompanyAttr(company.alias, 'size', size)
                }
                onCreate={async (size) => {
                  await updateWorkspaceFieldOptions(1, 'size', size);
                  changeCompanyAttr(company.alias, 'size', size);
                }}
                onDelete={(option) =>
                  handleOptionDelete(option, 'size', 'Size item')
                }
                onClear={() => changeCompanyAttr(company.alias, 'size', null)}
              />
            ),
            rawData: company.size,
          },
          {
            columnKey: ACCOUNT_COLUMNS.MONTHLY_SPEND,
            children: (
              <MainText>
                {company.monthly_spend
                  ? formatNumber(company.monthly_spend)
                  : company.monthly_spend}
              </MainText>
            ),
            rawData: company.monthly_spend,
          },
          {
            columnKey: ACCOUNT_COLUMNS.ANNUAL_REVENUE,
            children: (
              <MainText>
                {company.annual_revenue
                  ? formatNumber(company.annual_revenue)
                  : company.annual_revenue}
              </MainText>
            ),
            rawData: company.annual_revenue,
          },
          {
            columnKey: ACCOUNT_COLUMNS.PLAN,
            children: (
              <AttrSelectDropdown
                name={`company-list-${company.alias}-plan-dropdown`}
                view="list"
                selected={company.plan || null}
                options={planOptions}
                onSelect={(plan) =>
                  changeCompanyAttr(company.alias, 'plan', plan)
                }
                onCreate={async (plan) => {
                  await updateWorkspaceFieldOptions(1, 'plan', plan);
                  changeCompanyAttr(company.alias, 'plan', plan);
                }}
                onDelete={(option) =>
                  handleOptionDelete(option, 'plan', 'Plan item')
                }
                onClear={() => changeCompanyAttr(company.alias, 'plan', null)}
              />
            ),
            rawData: company.plan,
          },
          {
            columnKey: ACCOUNT_COLUMNS.RATING,
            children: (
              <AttrSelectDropdown
                name={`company-list-${company.alias}-rating-dropdown`}
                view="list"
                selected={company.rating || null}
                options={ratingOptions}
                onSelect={(rating) =>
                  changeCompanyAttr(company.alias, 'rating', rating)
                }
                onCreate={async (rating) => {
                  await updateWorkspaceFieldOptions(1, 'rating', rating);
                  changeCompanyAttr(company.alias, 'rating', rating);
                }}
                onDelete={(option) =>
                  handleOptionDelete(option, 'rating', 'Rating item')
                }
                onClear={() => changeCompanyAttr(company.alias, 'rating', null)}
              />
            ),
            rawData: company.rating,
          },
          {
            columnKey: ACCOUNT_COLUMNS.TYPE,
            children: (
              <AttrSelectDropdown
                name={`company-list-${company.alias}-type-dropdown`}
                view="list"
                selected={company.type || null}
                options={accountTypeOptions}
                onSelect={(type) =>
                  changeCompanyAttr(company.alias, 'type', type)
                }
                onCreate={async (type) => {
                  await updateWorkspaceFieldOptions(1, 'account_type', type);
                  changeCompanyAttr(company.alias, 'type', type);
                }}
                onDelete={(option) =>
                  handleOptionDelete(option, 'account_type', 'Type item')
                }
                onClear={() => changeCompanyAttr(company.alias, 'type', null)}
              />
            ),
            rawData: company.type,
          },
          {
            columnKey: ACCOUNT_COLUMNS.STAGE,
            children: (
              <AttrSelectDropdown
                name={`company-list-${company.alias}-stage-dropdown`}
                view="list"
                selected={company.stage || null}
                options={accountStageOptions}
                onSelect={(stage) =>
                  changeCompanyAttr(company.alias, 'stage', stage)
                }
                onCreate={async (stage) => {
                  await updateWorkspaceFieldOptions(1, 'account_stage', stage);
                  changeCompanyAttr(company.alias, 'stage', stage);
                }}
                onDelete={(option) =>
                  handleOptionDelete(option, 'account_stage', 'Stage item')
                }
                onClear={() => changeCompanyAttr(company.alias, 'stage', null)}
              />
            ),
            rawData: company.stage,
          },
          {
            columnKey: ACCOUNT_COLUMNS.PRIMARY_USECASE,
            children: (
              <AttrSelectDropdown
                name={`company-list-${company.alias}-usecase-dropdown`}
                view="list"
                selected={company.primary_usecase || null}
                options={accountPrimaryUsecseOptions}
                onSelect={(usecase) =>
                  changeCompanyAttr(
                    company.alias,
                    'primary_usecase',
                    usecase,
                  )
                }
                onCreate={async (usecase) => {
                  await updateWorkspaceFieldOptions(
                    1,
                    'account_primary_usecase',
                    usecase,
                  );
                  changeCompanyAttr(
                    company.alias,
                    'primary_usecase',
                    usecase,
                  );
                }}
                onDelete={(option) =>
                  handleOptionDelete(
                    option,
                    'account_primary_usecase',
                    'Primary Usecase item',
                  )
                }
                onClear={() =>
                  changeCompanyAttr(company.alias, 'primary_usecase', null)
                }
              />
            ),
            rawData: company.primary_usecase,
          },
          {
            columnKey: ACCOUNT_COLUMNS.COUNTRY,
            children: (
              <AttrSelectDropdown
                name={`company-list-${company.alias}-country-dropdown`}
                view="list"
                selected={company.country || null}
                options={countryOptions}
                onSelect={(country) =>
                  changeCompanyAttr(company.alias, 'country', country)
                }
                onClear={() =>
                  changeCompanyAttr(company.alias, 'country', null)
                }
              />
            ),
            rawData: company.country,
          },
          {
            columnKey: ACCOUNT_COLUMNS.FEEDBACK_COUNT,
            children: <MainText>{company.feedback_count || 0}</MainText>,
            rawData: company.feedback_count || 0,
          },
          {
            columnKey: ACCOUNT_COLUMNS.ACTIVE_STATUS,
            children: (
              <AttrSelectDropdown
                name={`company-list-${company.alias}-status-dropdown`}
                view="list"
                selected={
                  (company.status && COMPANY_STATUS_MAP.get(company.status)) ||
                  null
                }
                options={statusOptions}
                onSelect={(status) =>
                  changeCompanyAttr(
                    company.alias,
                    'status',
                    Number(status) as COMPANY_ACTIVE_STATUS,
                  )
                }
              />
            ),
            rawData: company.status || 1,
          },
          {
            columnKey: ACCOUNT_COLUMNS.NPS,
            children: <MainText>{company?.nps}</MainText>,
            rawData: company?.nps,
          },
          {
            columnKey: ACCOUNT_COLUMNS.CES,
            children: <MainText>{company?.ces}</MainText>,
            rawData: company?.ces,
          },
          {
            columnKey: ACCOUNT_COLUMNS.CSAT,
            children: <MainText>{company?.csat}</MainText>,
            rawData: company?.csat,
          },
          {
            columnKey: ACCOUNT_COLUMNS.CREATED_AT,
            children: (
              <MainText>
                {company.created_at && readableFormat(company.created_at)}
              </MainText>
            ),
            rawData: company.created_at,
          },
          {
            columnKey: ACCOUNT_COLUMNS.LAST_ACTIVITY,
            children: (
              <MainText>
                {company.latest_activity &&
                  readableFormat(company.latest_activity)}
              </MainText>
            ),
            rawData: company.latest_activity,
          },
          {
            columnKey: ACCOUNT_COLUMNS.MANAGER,
            children: (
              <AgentDropdown
                selected={company.agent || NOT_ASSIGNED_LABEL}
                name={`company-list-${company.alias}-agent-dropdown`}
                onSelect={(agent) =>
                  changeCompanyAttr(company.alias, 'agent', agent)
                }
                onClear={() => changeCompanyAttr(company.alias, 'agent', null)}
              />
            ),
            rawData: company.agent,
          },
          {
            columnKey: ACCOUNT_COLUMNS.KEY,
            children: <MainText>{company?.key || ''}</MainText>,
            rawData: company?.key,
          },
        ],
      });
      return acc;
    }, [] as DataTableRowT[]);
  }, [
    accountPrimaryUsecseOptions,
    accountStageOptions,
    accountTypeOptions,
    changeCompanyAttr,
    color.gray700,
    color.yellow500,
    companyMap,
    countryOptions,
    data,
    handleOptionDelete,
    industryOptions,
    isEditor,
    planOptions,
    ratingOptions,
    setSelectedCompany,
    sizeOptions,
    statusOptions,
    updateWorkspaceFieldOptions,
  ]);

  return result;
};

export { useCompanyHeader, useCompanyTableData };
